"use client"

/**
 * Post-result tool → grass-ID handoff card.
 *
 * 105 sessions/day enter the site on a /tools/* page and about half compute a
 * result, then dead-end: there was no cross-sell into the grass-ID flow at the
 * peak moment. This is that surface, and it renders ONLY when the tool does not
 * actually know the user's grass type (a defaulted "Kentucky bluegrass" is a
 * guess, not an answer). When the grass IS known, the tool's existing
 * RelatedTools block stays the next-step surface — never both in one slot.
 *
 * Design: the standard card recipe on a green-50 tint, so the result card above
 * stays the loudest surface on the page. Heading, tagline, effort line and an
 * action-button pill; the whole card is the photo-picker trigger.
 *
 * FORKED MARKUP, SHARED MECHANICS. It does not wrap components/blog/inline-scan-cta
 * because that component is a white glass mid-article banner with no styling seam
 * (no className prop) and it owns its own heading/tagline. The parts that
 * actually matter are reused verbatim: `useScanHandoff` (the iOS-safe photo
 * stash + navigation) and the paired cta_surface_shown / cta_clicked contract on
 * ONE identical `cta_location`.
 */

import { Camera, ArrowRight, Loader2 } from "lucide-react"
import { analytics, trackEvent } from "@/lib/analytics"
import { cn } from "@/lib/utils"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { useScanHandoff, SCAN_ACCEPT } from "@/hooks/use-scan-handoff"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"

const CTA_LOCATION = IDENTIFY_CTA.location.toolResultIdentify

interface ToolNextStepCardProps {
  /** Tool this card is rendered on. Rides on both analytics events as `tool_id`. */
  toolId: string
  heading: string
  tagline: string
  /**
   * The grass the tool silently assumed, e.g. "Kentucky bluegrass". Hosts name it
   * in `heading`; the card itself no longer renders a separate chip for it.
   */
  assumedGrassLabel?: string
  className?: string
}

export function ToolNextStepCard({
  toolId,
  heading,
  tagline,
  className,
}: ToolNextStepCardProps) {
  const { inputRef, handingOff, openPicker, onInputChange } = useScanHandoff(CTA_LOCATION)
  const impressionRef = useImpressionEvent<HTMLButtonElement>(
    CTA_LOCATION,
    { surface: "tool_next_step_card", tool_id: toolId },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <section className={className}>
      <button
        ref={impressionRef}
        type="button"
        onClick={() => {
          trackEvent("cta_clicked", {
            ...identifyCtaProps(CTA_LOCATION),
            surface: "tool_next_step_card",
            tool_id: toolId,
          })
          openPicker()
        }}
        disabled={handingOff}
        aria-label="Upload a photo of your grass to identify it"
        className={cn(
          "block w-full rounded-2xl border border-gray-200 bg-green-50 p-5 text-left text-gray-900 shadow-elevation-2 sm:p-6",
          handingOff ? "cursor-default" : "press hover-lift cursor-pointer",
        )}
      >
        <span className="flex items-start gap-3.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-green-200 bg-white">
            <Camera className="h-5 w-5 text-green-700" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block text-lg font-bold leading-snug tracking-tight text-gray-900 text-balance">
              {heading}
            </span>
            <span className="mt-1.5 block text-sm leading-relaxed text-gray-700 text-pretty">{tagline}</span>
          </span>
        </span>
        <span className="mt-5 flex flex-wrap items-center justify-between gap-3.5">
          <span className="text-sm text-gray-600">Takes about 10 seconds</span>
          <span className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-action px-5 text-sm font-semibold text-action-foreground shadow-sm">
            {handingOff ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Starting your ID
              </>
            ) : (
              <>
                Snap or upload a photo
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={SCAN_ACCEPT}
        capture="environment"
        className="hidden"
        onChange={onInputChange}
      />
    </section>
  )
}
